// HOW TO: Adding a brand
//  - Import the logo
//  - Add an entry to the LICENSE_BRANDS var, where the key is the `branding` license value (lower)
//  - If it's a story brand, add an entry to STORY_BRANDS (lower)
//  ALL VALUES WILL DEFAULT TO AMIRA VALUES UNLESS THEY ARE SPECIFICALLY OVERIDDEN

import { initi18n } from "../i18n/i18n";

// Logos
import AmiraLogo from "../images/AmiraLogo.svg";
import EPSLogo from "../images/partner-logos/EPS.png";
import EPSStandaloneLogo from "../images/partner-logos/EPS-Standalone.png";
// import CapstoneLogo from "../images/partner-logos/Capstone.png";
// import KiddomLogo from "../images/partner-logos/Kiddom.png"
import HighlightLogo from '../images/partner-logos/highlights_logo.svg';
import NweaLogo from '../images/partner-logos/nwea_logo.png';

// Standard Practice Logos
import AmiraStandardPracticeIcon from "../images/icons/AmiraStandardPractice.png";

// Skill Scaffold Logos
import AmiraSkillScaffoldIcon from "../images/icons/AmiraSkillScaffold.svg";
import { defaultCurriculumDisplayName } from "../components/Reports/containers/ReportSettings/SelectSkillsCurriculum/defaultCurriculumConfig";

// The list of Brands.  This is used to search through the story tags
const STORY_BRANDS = [
  'eps',
  'capstone',
  'kiddom',
  'highlights',
  'hmh',
  'eps-standalone-reading-assistant',
  'nwea-map-tutor'
]

const BRANDS_DATA = {
  amira: { 
    logo: AmiraLogo,
    logoAltText: 'Aira logo',
    footer: 'The first Intelligent Reading Assistant that listens, assesses & coaches, Amira accelerates reading mastery.',
    copyright: `Copyright © 2018-${new Date().getFullYear()} Amira Learning, Inc. All rights reserved. Terms of Use. Privacy Policy.`,
    tutorSkins: 'default',
    tutorName: 'Amira',
    companyName: 'Amira Learning',
    appName: 'Amira',
    standardPracticeIcon: AmiraStandardPracticeIcon,
    skillScaffoldIcon: AmiraSkillScaffoldIcon,
    defaultCurriculumName: defaultCurriculumDisplayName,
  },
  hmh: {
    footer: 'The first Intelligent Reading Assistant that listens, assesses & coaches, Amira accelerates reading mastery.',
    copyright: 'Copyright © by Houghton Mifflin Harcourt Publishing Company. All rights reserved.'
  }, 
  highlights: { 
    logo: HighlightLogo,
    logoAltText: 'Highlights logo',
    footer: 'The first Intelligent Reading Assistant that listens, assesses & coaches, Amira accelerates reading mastery.',
    copyright: 'Copyright © Highlights for Children, Inc., Columbus Ohio. All rights reserved.'
  },
  // USING AS EXAMPLE UNTIL NWA EXISTS
  eps: { 
    logo: EPSLogo,
    logoAltText: 'EPS logo',
    footer: 'The first Intelligent Reading Assistant that listens, assesses & coaches, Amira accelerates reading mastery.',
    copyright: '© EPS Operations, LLC. S.P.I.R.E.® 4th Edition. All rights reserved.',
    companyName: 'EPS',
    appName: 'Reading Assistant',
    tutorName: 'Amira',
    defaultCurriculumName: 'EPS Default',
  },
  // THESE VALUES NEED TO BE UPDATED CORRECTLY
  ['nwea-map-tutor']: {
    logo: NweaLogo,
    logoAltText: 'Nwea logo',
    footer: 'NEEDS UPDATE: The first Intelligent Reading Assistant that listens, assesses & coaches, Amira accelerates reading mastery.',
    copyright: `NEEDS UPDATE: Copyright © 2018-${new Date().getFullYear()} Amira Learning, Inc. All rights reserved. Terms of Use. Privacy Policy.`,
    companyName: 'NWEA',
    appName: 'Map Reading Fluency',
    tutorName: 'Tutor'
  },
  ["eps-standalone-reading-assistant"]: { 
    logo: EPSStandaloneLogo,
    logoAltText: 'Reading Assistant logo',
    footer: 'The first Intelligent Reading Assistant that listens, assesses & coaches.',
    copyright: '© EPS Operations, LLC. All rights reserved.',
    companyName: 'EPS',
    appName: 'Reading Assistant',
    tutorName: 'Amira'
  },
  // COMMENTED UNTIL ASSETS ARE FINISHED
  // capstone: { 
  //   logo: CapstoneLogo,
  //   logoAltText: 'Capstone logo',
  //   footer: 'The first Intelligent Reading Assistant that listens, assesses & coaches, Amira accelerates reading mastery.',
  //   copyright: 'Capstone Copyright Placeholder'
  // },
  // kiddom: { 
  //   logo: KiddomLogo,
  //   logoAltText: 'Kiddom logo',
  //   footer: 'The first Intelligent Reading Assistant that listens, assesses & coaches, Amira accelerates reading mastery.',
  //   copyright: 'Kiddom Copyright Placeholder'
  // },
}

export function SetBranding(branding = null) {
  localStorage.setItem('branding', branding);
  // Set these for direct .html (sessionEnd.html, etc) screen have access without react
  localStorage.setItem('brandedLogo', GetBrandedLogo());
  localStorage.setItem('brandedLogoAltText', GetBrandedLogoAltText());
  initi18n(GetBrandedAppName());
}

function getAndFormatBranding(rawBranding = null) {
  let branding = rawBranding;
  if (!branding) {
    branding = localStorage.getItem('branding');
  }
  if (branding) {
    branding = branding.toLowerCase();
  }

  return branding;
}

// Get the string name of the branded tutor character's Spine skin
export function GetBrandedTutorSkin() {
  const branding = getAndFormatBranding();
  return branding && BRANDS_DATA[branding] && BRANDS_DATA[branding].tutorSkin ? BRANDS_DATA[branding].tutorSkin : BRANDS_DATA.amira.tutorSkin;
}

// Get the string name of the branded tutor character
export function GetBrandedTutorName() {
  const branding = getAndFormatBranding();
  return branding && BRANDS_DATA[branding] && BRANDS_DATA[branding].tutorName ? BRANDS_DATA[branding].tutorName : BRANDS_DATA.amira.tutorName;
}

export function GetBrandedAppName() {
  const branding = getAndFormatBranding();
  return branding && BRANDS_DATA[branding] && BRANDS_DATA[branding].appName ? BRANDS_DATA[branding].appName : BRANDS_DATA.amira.appName;
}

export function GetBrandedCompanyName() {
  const branding = getAndFormatBranding();
  return branding && BRANDS_DATA[branding] && BRANDS_DATA[branding].companyName ? BRANDS_DATA[branding].companyName : BRANDS_DATA.amira.companyName;
}

// Further implementation thoughts:
// Do we like this way better than other getters here? it's more efficient maybe, only accesses needed values once instead of BRANDS_DATA[branding].X multiple times
export function GetBrandedStandardPracticeIcon() {
  const brand = BRANDS_DATA[getAndFormatBranding()];
  const standardPracticeIcon = brand ? brand.standardPracticeIcon : null;
  if (!standardPracticeIcon) {
    return BRANDS_DATA.amira.standardPracticeIcon;
  }
  return standardPracticeIcon;
}

export function GetBrandedSkillScaffoldIcon() {
  const brand = BRANDS_DATA[getAndFormatBranding()];
  const skillScaffoldIcon = brand ? brand.skillScaffoldIcon : null;
  if (!skillScaffoldIcon) {
    return BRANDS_DATA.amira.skillScaffoldIcon;
  }
  return skillScaffoldIcon;
}

export function GetBrandedLogo(rawBranding) {
  const branding = getAndFormatBranding(rawBranding);
  return branding && BRANDS_DATA[branding] ? BRANDS_DATA[branding].logo : BRANDS_DATA.amira.logo;
}

export function GetBrandedLogoAltText(rawBranding) {
  const branding = getAndFormatBranding(rawBranding);
  return branding && BRANDS_DATA[branding] ? BRANDS_DATA[branding].logoAltText : BRANDS_DATA.amira.logoAltText;
}

export function GetBrandedFooter(rawBranding) {
  const branding = getAndFormatBranding(rawBranding);
  return branding && BRANDS_DATA[branding] ? BRANDS_DATA[branding].footer : BRANDS_DATA.amira.footer;
}

export function GetBrandedCopyright(rawBranding) {
  const branding = getAndFormatBranding(rawBranding);
  return branding && BRANDS_DATA[branding] ? BRANDS_DATA[branding].copyright : BRANDS_DATA.amira.copyright;
}

export function GetBrandedCurriculumName(rawBranding) {
  const branding = getAndFormatBranding(rawBranding);
  return branding && BRANDS_DATA[branding] ? BRANDS_DATA[branding].defaultCurriculumName : BRANDS_DATA.amira.defaultCurriculumName;
}

// Tags for partners are ex: CP_EPS_*, so we need to loop through and check if each tag includes the brand. 
//   Then we can get the right data.  
export function GetStoryBranding(storyTags) {
  let brand = null;
  storyTags.forEach(tag => {
    STORY_BRANDS.forEach(storyBrand => {
      if (tag && tag.toLocaleLowerCase().includes(storyBrand)) {
        brand = BRANDS_DATA[storyBrand];
        return;
      }
    })

    if (brand) {
      return;
    }
  })

  return brand;
}